import { useMemo, useState } from "react";
import type { ContentBundle, NextActivity, TaskDefinition } from "@talent/protocol";
import { Creature } from "./creature.js";
import { COPY, taskHint, taskLabel } from "./copy.js";
import { isMuted, setMuted, unlockAudio } from "./sound.js";
import { familyClasses, familyLook } from "./accent.js";

/**
 * The home screen: pick a game.
 *
 * The previous version was a header showing a content-version string, an empty play area, and a
 * column of identical blue rectangles. A child saw a form. This is a shelf of things to touch.
 *
 * Three decisions here are not cosmetic:
 *
 * **One card per FAMILY, not per task.** The two A-13 arms of forward span are the same game
 * with and without a narrative wrapper, and showing both to one child destroys the experiment —
 * they would experience both conditions, so neither arm's data would mean anything. The arm is
 * assigned per profile instead, deterministically, so a child always gets the same one.
 *
 * **Five cards.** Published guidance for this age band puts a menu at five to seven items;
 * six tasks would have been six cards without the family collapse above.
 *
 * **The card is the touch target.** 104 px minimum, about 2.7 cm — well past the ~2 cm floor for
 * children, against ~44 px for an adult product. That is a measurement control and not a
 * courtesy: a mis-aimed tap that lands off-target is a motor artefact, and if it were scored it
 * would be recorded as a cognitive one.
 *
 * Layout is Tailwind against the generated theme, so `p-lg` and `SPACE.lg` are the same twenty
 * pixels. What stays inline is what varies per game — the accent gradient, the tinted lift and
 * the tilt — because those are computed from the family's colour rather than chosen from a scale.
 */

interface HomeScreenProps {
  bundle: ContentBundle;
  /**
   * Whose shelf this is, which decides the A-13 arm.
   *
   * **Null on a teacher's surface, and that is not a missing value.** Two arms of forward span are
   * one game, and a hash puts a child on one of them; a teacher has no arm and must not be given a
   * random one, because their own two screens would then name the same game differently — the games
   * list calling it one thing and the preview another. Null means the first arm, always, so every
   * teacher surface agrees.
   */
  profileId: string | null;
  /**
   * `offered` is the whole shelf, not just the tap.
   *
   * A choice is only informative against its alternatives — "picked the maze" says little; "picked
   * the maze when eleven other games were on screen" is a preference. The event schema has always
   * wanted both, and nothing was sending either.
   */
  onPlay: (task: TaskDefinition, offered: readonly string[]) => void;
  /**
   * What the teacher asked for, or what the system suggests — with the level to open at.
   *
   * Null while it is still being fetched, so the shelf appears immediately rather than waiting on a
   * request a child did not ask for.
   */
  next: NextActivity | null;
  onPlayNext: (task: TaskDefinition, next: NextActivity) => void;
  /**
   * The games this child's class has been given, or null for "every game" (ADR-014).
   *
   * Null rather than an empty array for the unrestricted case, because those are opposite meanings and
   * an empty array is what a locked class with nothing open genuinely looks like. Null is the teacher's
   * preview, which is not a class and has nothing to be restricted by.
   */
  openTasks: readonly string[] | null;
  /**
   * Headings to lay the shelf out under, or nothing for one flat grid.
   *
   * **The child's shelf passes nothing and is unchanged by this.** A four-year-old navigates by
   * picture and does not read a heading, and grouping the tiles would only make the shelf change
   * shape — the one thing `openTasks` above is careful not to do.
   *
   * The teacher's preview passes the seven areas of the week, because a teacher browsing
   * twenty-five games to try one has the same question the games screen answers and had no answer
   * here. Same tiles, same order within a group, headings over the top.
   */
  sections?: readonly { label: string; taskIds: readonly string[] }[];
  onSignOut: () => void;
  /**
   * Drops the header — the sound toggle and the sign-out.
   *
   * For the teacher's preview, which supplies its own heading and where neither control belongs: a
   * teacher's sign-out lives with the rest of their account controls on the roster, and a second
   * page title above the shelf reads as two screens stacked.
   */
  hideChrome?: boolean;
}

/**
 * Picks one task per family, stable for a given profile.
 *
 * Hashed from the profile id rather than drawn randomly: an arm that changed between sessions
 * would put one child in both conditions across their history, which is the same corruption as
 * showing both at once, only harder to notice.
 */
function oneTaskPerFamily(
  tasks: readonly TaskDefinition[],
  profileId: string | null,
): TaskDefinition[] {
  let hash = 2166136261 >>> 0;
  for (let i = 0; i < (profileId ?? "").length; i++) {
    hash ^= (profileId as string).charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0;
  }

  const byFamily = new Map<string, TaskDefinition[]>();
  for (const task of tasks) {
    const list = byFamily.get(task.family) ?? [];
    list.push(task);
    byFamily.set(task.family, list);
  }

  return [...byFamily.values()].map((arms) => {
    // Sorted first, so the choice does not depend on the order the bundle happens to list them.
    const sorted = [...arms].sort((a, b) => a.id.localeCompare(b.id));
    // No profile means no arm to be on: the first one, so every teacher screen names it the same.
    return (profileId === null ? sorted[0] : sorted[hash % sorted.length]) as TaskDefinition;
  });
}

export function HomeScreen({
  bundle,
  profileId,
  onPlay,
  next,
  onPlayNext,
  onSignOut,
  openTasks,
  sections,
  hideChrome = false,
}: HomeScreenProps) {
  const [muted, setMutedState] = useState(isMuted);
  /**
   * The whole shelf, with each tile knowing whether it is open.
   *
   * **Shut games are shown, greyed, rather than hidden.** Hiding them makes the shelf change shape
   * every time a teacher opens something, so a child never learns where anything is — and it hides the
   * best thing about a locked shelf, which is that a four-year-old can see what is coming. A grey tile
   * with a padlock is a promise; an absent tile is nothing at all.
   *
   * One arm per family is picked from the WHOLE bundle rather than from the open subset, so which arm
   * a child gets never depends on what their teacher has opened — the A-13 assignment has to be stable
   * across a child's history (`oneTaskPerFamily`).
   */
  const games = useMemo(() => {
    const chosen = oneTaskPerFamily(bundle.tasks, profileId).map((task) => ({
      task,
      open: openTasks === null || openTasks.includes(task.id),
    }));
    /**
     * Open first, shut after — and stable within each group.
     *
     * A four-year-old scans from the top and stops at the first thing they like. With the two mixed,
     * that is as likely to be a padlock as a game, and every session starts with a tap that does
     * nothing. Sorting also means the shelf does not reshuffle when a teacher opens something: the
     * newly-open tile moves up, and everything a child already knew the position of stays put relative
     * to its own group.
     */
    return chosen.sort((a, b) => Number(b.open) - Number(a.open));
  }, [bundle.tasks, openTasks, profileId]);
  const openGames = useMemo(() => games.filter((g) => g.open), [games]);
  /**
   * The suggested activity, resolved against the bundle.
   *
   * Null when the server has nothing to suggest — a child who has mastered everything available —
   * and also when the named task is not in this bundle, which would mean a pin that has moved under
   * the suggestion. Both come out as "no card", which is the honest rendering of both.
   */
  const suggested = useMemo(() => {
    const task = bundle.tasks.find((t) => t.id === next?.taskId) ?? null;
    // A suggestion pointing at a game the class does not have is not a suggestion. It happens when a
    // teacher withdraws a game after handing out its goal, and the honest rendering is no card.
    if (!task || openTasks === null || openTasks.includes(task.id)) return task;
    return null;
  }, [bundle.tasks, next?.taskId, openTasks]);

  return (
    <div className="mx-auto min-h-full max-w-[560px] px-lg pt-lg pb-xxl">
      {!hideChrome && (
      <header className="mb-lg flex items-center justify-between gap-md">
        {/* The sound toggle is a child-reachable control, so it gets a real target too. */}
        <button
          onClick={() => {
            const next = !muted;
            setMuted(next);
            setMutedState(next);
            // Audio can only start from a gesture. Turning sound ON is that gesture.
            if (!next) unlockAudio();
          }}
          aria-label={muted ? COPY.child.soundOff : COPY.child.soundOn}
          className="grid size-[52px] place-items-center rounded-pill bg-card text-2xl shadow-card"
        >
          {/* An emoji, deliberately: it needs no asset, reads at a glance, and a child knows
              both of these symbols before they can read. */}
          <span aria-hidden>{muted ? "🔇" : "🔊"}</span>
        </button>

        <button
          onClick={onSignOut}
          className="rounded-pill bg-card/70 px-md py-sm text-small text-muted"
        >
          {COPY.child.signOut}
        </button>
      </header>
      )}

      {/*
        * What the teacher asked for, above the shelf.
        *
        * One card, with the reason it is being offered, and a level to open at so the child never
        * replays what they already know. It sits ABOVE the games rather than replacing them: free
        * choice is kept deliberately (ADR-011), because what a child picks when nothing is asked of
        * them is the one thing an assignment cannot tell you — and a shelf that only ever showed one
        * option would stop producing that.
        */}
      {suggested && next && (
        <TodayCard task={suggested} next={next} onPlay={() => onPlayNext(suggested, next)} />
      )}
      {next?.reason === "all-caught-up" && (
        <p className="mb-lg rounded-lg bg-card px-md py-md text-center text-body text-muted">
          {COPY.child.today.caughtUp}
        </p>
      )}
      {suggested && (
        <p className="mb-md text-center text-small text-muted">{COPY.child.freeChoice}</p>
      )}

      {/*
        * A grid of tiles rather than a column of rows.
        *
        * With eight families a full-width list is a long scroll, and this age band navigates by
        * picture — a tile gives the creature roughly three times the area a row did and puts the
        * whole shelf in one view. `auto-fill` with a minimum does the responsive work in CSS: two
        * columns on a phone, more on a tablet, without a breakpoint to maintain.
        *
        * The minimum is 148 px, so every tile clears the ~76 px touch floor twice over in both
        * directions — the reason that floor exists is that a mis-aimed tap is a motor artefact,
        * and here there is nothing adjacent to mis-hit.
        */}
      {/*
        * Nothing open yet.
        *
        * Said as something that is about to happen rather than as a refusal: every game is shut until a
        * teacher opens it (ADR-014), so an empty shelf is the ordinary first state of a new class and
        * not a fault the child can do anything about.
        */}
      {/*
        * Said once, above the shelf, when nothing has been opened yet.
        *
        * The tiles below are all grey in that state, which is legible but not self-explanatory — a
        * child staring at sixteen locked games deserves a sentence saying whose move it is.
        */}
      {openGames.length === 0 && (
        <div className="mb-md grid place-items-center gap-sm rounded-lg bg-card px-md py-lg text-center shadow-card">
          <Creature kind="firefly" accent="sunny" size={72} />
          <p className="text-body">{COPY.child.nothingOpen}</p>
          <p className="text-small text-muted">{COPY.child.nothingOpenHint}</p>
        </div>
      )}

      {(() => {
        const tile = (entry: (typeof games)[number], index: number) => (
          <GameTile
            key={entry.task.id}
            task={entry.task}
            index={index}
            locked={!entry.open}
            // Only the open ones count as "offered": a choice is informative against the
            // alternatives the child could actually have taken.
            onPlay={() => onPlay(entry.task, openGames.map((g) => g.task.id))}
          />
        );
        const grid = (children: React.ReactNode) => (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(148px,1fr))] gap-md">
            {children}
          </div>
        );

        if (!sections) return grid(games.map(tile));

        /**
         * Laid out under headings, with anything unaccounted for at the end.
         *
         * A game belonging to no section would otherwise vanish from the only screen that can start
         * it. Nothing in the bundle is in that position today, and a shelf that quietly drops a game
         * is a worse failure than a heading-less row at the bottom.
         *
         * `index` runs across the whole shelf rather than restarting per section, because it feeds
         * the tiles' scatter of rotations — restarting it would make every section open with the
         * same tilt.
         */
        let index = 0;
        const placed = new Set<string>();
        const blocks = sections.flatMap((section) => {
          const mine = games.filter((g) => section.taskIds.includes(g.task.id));
          if (mine.length === 0) return [];
          for (const g of mine) placed.add(g.task.id);
          return [
            <section key={section.label} className="mb-lg">
              <h2 className="mb-sm px-1 text-heading">{section.label}</h2>
              {grid(mine.map((entry) => tile(entry, index++)))}
            </section>,
          ];
        });
        const rest = games.filter((g) => !placed.has(g.task.id));
        return (
          <>
            {blocks}
            {rest.length > 0 && grid(rest.map((entry) => tile(entry, index++)))}
          </>
        );
      })()}
    </div>
  );
}

/** The reason line under the card, in the child's own words rather than the recommender's. */
const REASON_LABEL: Record<NextActivity["reason"], string> = {
  assigned: COPY.child.today.assigned,
  unfinished: COPY.child.today.unfinished,
  progression: COPY.child.today.progression,
  starting: COPY.child.today.starting,
  "all-caught-up": COPY.child.today.caughtUp,
};

function TodayCard({
  task,
  next,
  onPlay,
}: {
  task: TaskDefinition;
  next: NextActivity;
  onPlay: () => void;
}) {
  const look = familyLook(task.family);
  const accent = familyClasses(task.family);

  return (
    <button
      onClick={() => {
        unlockAudio();
        onPlay();
      }}
      className={`mb-lg flex w-full items-center gap-md rounded-lg bg-card px-md py-md text-start transition-[transform,box-shadow] duration-200 ease-bounce ${accent.shadow}`}
    >
      <span className={`grid size-[76px] shrink-0 place-items-center rounded-pill ${accent.disc}`}>
        <Creature kind={look.creature} accent={look.accent} size={58} step={0} />
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-caption text-muted">{REASON_LABEL[next.reason]}</span>
        <span className="text-title leading-[1.3]">{taskLabel(task.id)}</span>
      </span>
      <span className={`ms-auto shrink-0 rounded-pill px-md py-sm text-small ${accent.disc}`}>
        {COPY.child.today.start}
      </span>
    </button>
  );
}

/**
 * A scatter of tiny rotations, so the grid reads as stickers laid down rather than a table.
 *
 * Whole class strings rather than degrees fed to a transform: Tailwind finds classes by scanning
 * source text, so a value built at runtime produces no CSS and the tile simply sits square with
 * nothing to indicate why.
 */
const TILT = [
  "-rotate-[1.1deg]",
  "rotate-[0.7deg]",
  "-rotate-[0.5deg]",
  "rotate-[1.2deg]",
  "rotate-[0.4deg]",
  "-rotate-[0.9deg]",
  "rotate-[1deg]",
  "-rotate-[0.6deg]",
] as const;

function GameTile({
  task,
  index,
  locked,
  onPlay,
}: {
  task: TaskDefinition;
  index: number;
  /** The teacher has not opened this one yet (ADR-014). Shown, greyed, and not tappable. */
  locked: boolean;
  onPlay: () => void;
}) {
  const look = familyLook(task.family);
  const accent = familyClasses(task.family);
  const hint = taskHint(task.id);

  return (
    <button
      disabled={locked}
      onClick={() => {
        // The first tap in a session is what lets audio start at all.
        unlockAudio();
        onPlay();
      }}
      // min-h is comfortably past the touch floor in both directions, and tall enough that the
      // name has two lines before it has to clamp.
      //
      // A locked tile keeps its shape and loses its colour and its lift: it is plainly the same game,
      // plainly not available yet, and plainly not a thing to press.
      className={`relative flex min-h-[178px] w-full flex-col items-center justify-start gap-sm rounded-lg px-md pt-lg pb-md text-center transition-[transform,box-shadow] duration-200 ease-bounce ${
        locked
          ? "bg-cream"
          : `bg-card ${accent.shadow} ${TILT[index % TILT.length]}`
      }`}
    >
      {/* The creature in a tinted disc: the colour anchor a child scans by, and the reason a
          tile works at all for someone who cannot read the name under it. */}
      <span
        className={`grid size-[92px] shrink-0 place-items-center rounded-pill ${
          locked ? "bg-ink/6 opacity-45 grayscale" : accent.disc
        }`}
      >
        <Creature kind={look.creature} accent={look.accent} size={68} step={index} />
      </span>

      <span className={`text-heading leading-[1.35] ${locked ? "text-muted" : ""}`}>
        {taskLabel(task.id)}
      </span>
      {hint && !locked && (
        // Clamped: a tile has room for a hint, not for a paragraph, and an overflowing line
        // would push the grid rows out of alignment with each other.
        <span className="line-clamp-2 text-caption font-normal text-muted">{hint}</span>
      )}

      {/*
        * A padlock, drawn rather than written.
        *
        * The grey says "not now" to an adult; the padlock says it to a four-year-old, who has seen one
        * on a door. Nothing here says why — that is between them and their teacher.
        */}
      {locked && (
        <span className="absolute end-sm top-sm grid size-7 place-items-center rounded-pill bg-card text-muted shadow-card">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            aria-hidden
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          >
            <rect x="4" y="10.5" width="16" height="10" rx="3" />
            <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
          </svg>
        </span>
      )}
    </button>
  );
}
